import { useCallback } from "react";

// 从环境变量或配置获取 API URL
const EDUGATE_API_URL = process.env.REACT_APP_EDUGATE_API_URL || "http://localhost:8081";

/**
 * 密码处理相关 Hook
 * 封装密码修改、模型选择密码验证等操作
 */
const usePasswordHandlers = ({
  currentUser,
  passwordForm,
  setPasswordForm,
  setPasswordDialogOpen,
  modelType,
  setModelType,
  isAdvancedUnlocked,
  setIsAdvancedUnlocked,
  advancedPassword,
  setAdvancedPassword,
  setShowAdvancedPasswordDialog,
  ADVANCED_MODE_CONFIG,
}) => {
  // 处理密码表单输入变化
  const handlePasswordChange = useCallback((e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, [setPasswordForm]);

  // 处理密码修改提交
  const handlePasswordSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // 简单的表单验证
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      alert("请填写所有密码字段");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("新密码和确认密码不匹配");
      return;
    }
    
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studentNumber: currentUser.student_number,
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert("密码修改成功");
        setPasswordDialogOpen(false);
        // 清空表单
        setPasswordForm({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        alert(data.message || "密码修改失败");
      }
    } catch (error) {
      console.error("密码修改错误:", error);
      alert("密码修改失败，请稍后再试");
    }
  }, [currentUser, passwordForm, setPasswordDialogOpen, setPasswordForm]);

  // 处理模型类型切换
  const handleModelTypeChange = useCallback((e) => {
    const newModelType = e.target.value;
    // 如果是访客用户且尝试设置为deepseek，则阻止
    if (currentUser?.user_type === "guest" && newModelType === "deepseek") {
      console.log("访客用户不能使用DeepSeek");
      return;
    }
    
    // 如果选择Advanced模式且未解锁，显示密码对话框
    if (newModelType === "advanced" && !isAdvancedUnlocked) {
      setShowAdvancedPasswordDialog(true);
      return;
    }
    
    console.log("选择模型类型:", newModelType);
    setModelType(newModelType);
  }, [currentUser, isAdvancedUnlocked, setModelType, setShowAdvancedPasswordDialog]);

  // 处理Advanced密码验证
  const handleAdvancedPasswordSubmit = useCallback(() => {
    if (advancedPassword === ADVANCED_MODE_CONFIG.password) {
      setIsAdvancedUnlocked(true);
      setModelType("advanced");
      setShowAdvancedPasswordDialog(false);
      setAdvancedPassword("");
      console.log("Advanced模式已解锁");
    } else {
      alert(ADVANCED_MODE_CONFIG.passwordError);
      setAdvancedPassword("");
    }
  }, [
    advancedPassword,
    ADVANCED_MODE_CONFIG,
    setAdvancedPassword,
    setIsAdvancedUnlocked,
    setModelType,
    setShowAdvancedPasswordDialog,
  ]);

  // 关闭密码对话框
  const handleAdvancedPasswordCancel = useCallback(() => {
    setShowAdvancedPasswordDialog(false);
    setAdvancedPassword("");
  }, [setAdvancedPassword, setShowAdvancedPasswordDialog]);

  return {
    handlePasswordChange,
    handlePasswordSubmit,
    handleModelTypeChange,
    handleAdvancedPasswordSubmit,
    handleAdvancedPasswordCancel,
  };
};

export default usePasswordHandlers;
